/**
 * Conversion helpers between the NYCBS SFTP-push clause shape
 * (`{ operator, clauses: [...] }`) and the QueryBuilder tree
 * (`{ type, id, conjunction, children }`), used by the Fetch Filter and Status
 * Update Conditions sections.
 *
 * `fieldKey` selects which key carries the field name: `"column"` for
 * fetch_filter / status_update_conditions, `"field"` for response-mapping
 * special cases. `writeOp` controls whether the symbolic operator (`=` / `!=`
 * / `IN` / `NOT IN`) is emitted on save (column-style clauses) or omitted
 * (field-style clauses, where equality is implied).
 */
import { v4 as uuidv4 } from "uuid";
import { createEmptyGroup } from "../../../../../components/queryBuilder/queryBuilderUtils";
import {
  SftpClause,
  SftpClauseGroup,
} from "../../../../../data-model/nycbsSftpPushConfig";
import { QueryGroup, QueryNode } from "../../../../../data-model/queryBuilder";

const EMPTY_VALUE_OPERATORS = new Set(["is_empty", "is_not_empty"]);

const MULTI_VALUE_OPERATORS = new Set([
  "in",
  "not_in",
  "is_any_of",
  "is_not_any_of",
  "IN",
  "NOT IN",
]);

/** Maps a stored symbolic operator to the builder-canonical operator value. */
function opToCanonical(op: string | undefined): string {
  if (!op || op === "=") return "equals";
  if (op === "!=" || op === "≠") return "not_equals";
  if (op === "IN") return "in";
  if (op === "NOT IN") return "not_in";
  return op;
}

/** Maps a builder-canonical operator value back to the stored symbolic operator. */
function canonicalToOp(operator: string): string {
  if (operator === "equals") return "=";
  if (operator === "not_equals") return "!=";
  if (operator === "in" || operator === "is_any_of") return "IN";
  if (operator === "not_in" || operator === "is_not_any_of") return "NOT IN";
  return operator;
}

/**
 * Coerces a stored clause value into the string | string[] the builder expects.
 *
 * @param op - Stored or canonical operator
 * @param value - Stored clause value (string or list for IN / NOT IN)
 * @return Builder value for the condition row
 */
function clauseValueToQueryValue(
  op: string | undefined,
  value: string | string[] | undefined,
): string | string[] {
  const canonicalOp = opToCanonical(op);
  if (EMPTY_VALUE_OPERATORS.has(canonicalOp)) {
    return "";
  }
  if (MULTI_VALUE_OPERATORS.has(canonicalOp) || MULTI_VALUE_OPERATORS.has(op ?? "")) {
    if (Array.isArray(value)) {
      return value.map((entry) => String(entry));
    }
    if (value != null && String(value).trim() !== "") {
      return [String(value)];
    }
    return [];
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? String(value[0]) : "";
  }
  return value != null ? String(value) : "";
}

/**
 * Coerces a builder condition value into the stored clause value shape.
 *
 * @param operator - Builder-canonical operator
 * @param value - Builder condition value
 * @return Stored clause value (list for IN / NOT IN, string otherwise)
 */
function queryValueToClauseValue(
  operator: string,
  value: string | string[],
): string | string[] | undefined {
  if (EMPTY_VALUE_OPERATORS.has(operator)) {
    return undefined;
  }
  if (MULTI_VALUE_OPERATORS.has(operator)) {
    const values = Array.isArray(value)
      ? value.map((entry) => String(entry).trim()).filter((entry) => entry !== "")
      : String(value).trim() !== ""
        ? [String(value)]
        : [];
    return values;
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? String(value[0]) : "";
  }
  return value;
}

function clauseToNode(
  clause: SftpClause,
  fieldKey: "column" | "field",
): QueryNode {
  if (Array.isArray(clause.clauses)) {
    return clausesToQueryGroup(
      clause.operator ?? "AND",
      clause.clauses,
      fieldKey,
    );
  }
  const fieldName = clause[fieldKey] ?? clause.column ?? clause.field ?? "";
  return {
    type: "condition",
    id: uuidv4(),
    field: fieldName,
    operator: opToCanonical(clause.op),
    value: clauseValueToQueryValue(clause.op, clause.value),
  };
}

/**
 * Converts a stored clause list into a QueryBuilder value tree.
 *
 * @input operator: Group conjunction (`AND` / `OR`)
 * @input clauses: Stored clause list (may be undefined)
 * @input fieldKey: Which key carries the field name
 * @return QueryGroup: Builder tree; an empty group when there are no clauses
 */
export function clausesToQueryGroup(
  operator: "AND" | "OR" | undefined,
  clauses: SftpClause[] | undefined,
  fieldKey: "column" | "field",
): QueryGroup {
  if (!Array.isArray(clauses) || clauses.length === 0) {
    return {
      ...createEmptyGroup(),
      conjunction: operator === "OR" ? "OR" : "AND",
    };
  }
  return {
    type: "group",
    id: uuidv4(),
    conjunction: operator === "OR" ? "OR" : "AND",
    children: clauses.map((clause) => clauseToNode(clause, fieldKey)),
  };
}

function nodeToClause(
  node: QueryNode,
  fieldKey: "column" | "field",
  writeOp: boolean,
): SftpClause {
  if (node.type === "group") {
    return {
      operator: node.conjunction,
      clauses: node.children
        .filter(isMeaningfulNode)
        .map((child) => nodeToClause(child, fieldKey, writeOp)),
    };
  }
  const clause: SftpClause = { [fieldKey]: node.field };
  if (writeOp) {
    clause.op = canonicalToOp(node.operator);
  }
  const storedValue = queryValueToClauseValue(node.operator, node.value);
  if (storedValue !== undefined) {
    clause.value = storedValue;
  }
  return clause;
}

/**
 * Converts a QueryBuilder value tree back into the stored clause shape.
 *
 * @input group: Builder root group
 * @input fieldKey: Which key carries the field name
 * @input writeOp: Whether to emit the symbolic operator on each clause
 * @return SftpClauseGroup: Stored `{ operator, clauses }` group
 */
export function queryGroupToClauses(
  group: QueryGroup,
  fieldKey: "column" | "field",
  writeOp: boolean,
): SftpClauseGroup {
  return {
    operator: group.conjunction === "OR" ? "OR" : "AND",
    clauses: group.children
      .filter(isMeaningfulNode)
      .map((child) => nodeToClause(child, fieldKey, writeOp)),
  };
}

/** Drops blank rows: a condition needs a field; a group needs a meaningful child. */
function isMeaningfulNode(node: QueryNode): boolean {
  if (node.type === "group") {
    return node.children.some(isMeaningfulNode);
  }
  return node.field.trim() !== "";
}

export { canonicalToOp, opToCanonical };
